/**
 * Supabase Todo Service
 *
 * Supabase-backed implementation of {@link ITodoService} — the only todo data
 * implementation, exposed via
 * {@link module:features/todo-list/services/todoService}.
 *
 * Mapping notes (app camelCase ⇄ DB snake_case):
 *  - tasks.order ⇄ tasks.sort_order
 *  - task_lists.title ⇄ task_lists.name, task_lists.createdBy ⇄ owner_id
 *  - dueDate ⇄ due_date (DATE), dueTime ⇄ due_time (TIME, stored HH:mm[:ss])
 *
 * Known schema gaps (documented for QA, no column to persist into):
 *  - Task.eventId, Task.completedAt, Task.completedBy have no `tasks` columns.
 *    The last two are not inert: `todoStore.toggleTaskStatus` stamps them on
 *    the ticked task and `TaskCard` prints "завършена на <date>" from them, so
 *    that line is real until the next `load()`/`refetch()` rebuilds the task
 *    from a row that never carried it, and then it is gone. Closing that needs
 *    `tasks.completed_at`/`completed_by`, which migration 028 does not add.
 *
 * TaskList.emoji and TaskList.color are NOT in that list any more — migration
 * 028 gave `task_lists` an `emoji text` and a `color text` column, and they are
 * written and read below like any other field.
 *
 * OFFLINE WRITES, AND WHY THE GATE HAS TWO SIDES
 *   "The network is down" arrives here in two shapes and only one of them is
 *   cheap to detect. `navigator.onLine === false` is the synchronous one and is
 *   checked first, before anything touches the wire. The other is a browser
 *   that reports `onLine === true` over a captive portal, a dead uplink or a
 *   DNS failure — the ordinary mobile case — where the write is issued, never
 *   reaches Postgres, and comes back through postgrest-js as an error envelope
 *   rather than as a rejection. So every mutating method also classifies its
 *   own failure with {@link isTransientWriteFailure} and enqueues on the
 *   transport class ONLY. A refusal (RLS, validation, a constraint violation)
 *   is never queued: it would replay forever and block the FIFO queue for
 *   every other table.
 *
 *   Everything a write needs is therefore resolved either offline-tolerantly
 *   ({@link SupabaseTodoService.resolveProfileId}, and `getCurrentFamilyId`
 *   through the in-session override and a local session read) or BELOW the
 *   offline gate. A network read above the gate is what made an offline
 *   checkbox tap vanish — see {@link lastKnownTaskStatus} — and what made a
 *   create vanish on a dead uplink the browser still called "online".
 *
 * WHAT A RESOLVED WRITE MEANS HERE
 *   Every mutating method obeys one rule: RESOLVING means the write is safe —
 *   it landed in Postgres, or it is on the offline replay queue — and REJECTING
 *   means it is not saved and the caller must undo. There is no third value.
 *
 *   That rule exists because an RLS `USING` clause on UPDATE/DELETE FILTERS
 *   rows instead of raising, so PostgREST answers the write the database
 *   refused with zero rows and `error === null`. `updateTask` folded that into
 *   `return null`, `deleteTask` into `return !error`, and `persistTodoMutation`
 *   in `todoStore` only rolls back, records `state.error` and toasts from
 *   INSIDE a `.catch()` — so a resolved refusal reached no failure handler at
 *   all. A child renamed a shared list, `task_lists_modify_parent` refused it,
 *   nothing was toasted, and the new name sat on screen until the next focus
 *   refetch silently took it away.
 *
 *   Two consequences worth knowing before editing anything below. Every DELETE
 *   asks for its rows back with `.select('id')`, because that is the only way
 *   to see a refusal at all; and a DELETE that matched nothing asks one
 *   confirming SELECT before deciding what it means — see
 *   {@link SupabaseTodoService.classifyEmptyDelete}. Nothing about this is
 *   enforced by the compiler: every store call site is written
 *   `async () => { await todoService.x(…); }`, which is assignable to
 *   `run: () => Promise<void>` whatever the method returns, so the throw is the
 *   entire safety mechanism.
 *
 * @module features/todo-list/services/supabaseTodoService
 */

import { getSupabaseClient } from '@/shared/clients/supabaseClient';
import {
  getCurrentFamilyId,
  getCurrentProfileId,
} from '@/shared/lib/familyContext';
import {
  enqueueSupabaseMutation,
  isTransientWriteFailure,
} from '@/shared/lib/offlineMutationQueue';
import { sanitizeText } from '@/shared/lib/sanitize';
import { monitoringService } from '@/shared/services/monitoringService';
import type {
  Json,
  Tables,
  TablesInsert,
  TablesUpdate,
} from '@/shared/types/database';
import type {
  CreateTaskInput,
  CreateTaskListInput,
  Task,
  TaskCategory,
  TaskList,
  TaskRecurrenceFrequency,
  TaskRecurrenceRule,
  TaskStatus,
  UpdateTaskInput,
  UpdateTaskListInput,
} from '../types/task.types';
import { calculateListStats, type ListStats } from '../utils/listStats';
import type { ITodoService } from './todoService.types';

type TaskRow = Tables<'tasks'>;
type TaskListRow = Tables<'task_lists'>;
type TaskCategoryRow = Tables<'task_categories'>;

// ============================================
// Mappers (snake_case row ⇄ camelCase entity)
// ============================================

/**
 * Last status seen for every task row that has passed through
 * {@link rowToTask} — the local truth an OFFLINE toggle flips.
 *
 * `toggleTaskStatus` has no other way to learn a task's current status while
 * the network is down. Reading it back costs a round trip, and that round trip
 * IS the bug this map closes: postgrest-js folds a failed fetch into
 * `{ data: null, error }`, `getTask` turns that into `null`, and the toggle
 * returned early without queueing anything and without throwing — the tick
 * stayed on screen until the next refetch replaced `tasks` wholesale and took
 * it away with nothing to explain it. The two obvious alternatives are both
 * closed: importing the store from here makes a `service → store → service`
 * cycle that `npm run deps:check` rejects, and `ITodoService.toggleTaskStatus`
 * takes only an id, so the caller cannot hand the status over either.
 *
 * `rowToTask` is the single funnel every task row reaches the app through —
 * `getTasks`, `getTask`, `createTask`, `updateTask` and the realtime
 * INSERT/UPDATE echo in `useRealtimeTasks` all map through it — so recording
 * here keeps this map in step with exactly what the store is rendering, which
 * is what the user is tapping. Bounded by the tasks one session has seen, and
 * cleared by {@link SupabaseTodoService.reset}.
 */
const lastKnownTaskStatus = new Map<string, TaskStatus>();

/** The frequencies {@link TaskRecurrenceRule} admits, as a runtime list. */
const TASK_RECURRENCE_FREQUENCIES: readonly TaskRecurrenceFrequency[] = [
  'daily',
  'weekly',
  'monthly',
  'yearly',
];

/**
 * Turn a `tasks.recurrence` jsonb payload into a {@link TaskRecurrenceRule},
 * or into nothing at all.
 *
 * THE COLUMN IS `Json`, NOT A RULE. jsonb holds whatever was written into it —
 * by this bundle, by a bundle two releases old, by a hand-run `update` during a
 * support session — and Postgres validates none of its shape. The read path
 * used to assert the shape anyway (`row.recurrence as unknown as
 * TaskRecurrenceRule`), which is the one cast TypeScript cannot check and the
 * compiler stops arguing about: a row holding `"weekly"`, `{}` or a legacy
 * `{ freq: 'WEEKLY' }` was handed to the UI as a rule, and the `undefined` only
 * surfaced several components deep, wherever `.frequency` was first read.
 *
 * A malformed payload therefore degrades to a NON-RECURRING task instead of
 * throwing. `rowToTask` is the single funnel every task row reaches the app
 * through, so a throw here would blank an entire list because of one bad row —
 * losing the repeat is the smaller loss, and it is visible and fixable in the
 * edit form.
 *
 * Deliberately NOT the zod schema from `../schemas/task.schemas`: that module
 * validates FORM input (what a user may submit) and this one validates STORED
 * input (what the database happens to hold). They agree today and must stay
 * free to diverge — tightening the form must never make old rows unreadable.
 * The one place they are kept in step on purpose is the `interval` default:
 * both read a missing interval as "every 1".
 */
function parseRecurrence(value: Json): TaskRecurrenceRule | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined;
  }
  const { frequency, interval, endDate, count } = value;
  if (
    typeof frequency !== 'string' ||
    !TASK_RECURRENCE_FREQUENCIES.includes(frequency as TaskRecurrenceFrequency)
  ) {
    return undefined;
  }
  const isPositiveInt = (n: unknown): n is number =>
    typeof n === 'number' && Number.isInteger(n) && n >= 1;
  return {
    frequency: frequency as TaskRecurrenceFrequency,
    interval: isPositiveInt(interval) ? interval : 1,
    ...(typeof endDate === 'string' && { endDate }),
    ...(isPositiveInt(count) && { count }),
  };
}

/**
 * Serialise a rule for the jsonb column — the mirror of
 * {@link parseRecurrence}, and the reason the write path no longer casts.
 *
 * Spelling the object out keeps what is WRITTEN inside what can be READ: a
 * field added to `TaskRecurrenceRule` without a matching line here fails to
 * persist, which is a bug the next reader can see, rather than landing in jsonb
 * as a key the parser silently drops.
 */
function recurrenceToJson(rule: TaskRecurrenceRule): Json {
  return {
    frequency: rule.frequency,
    interval: rule.interval,
    ...(rule.endDate !== undefined && { endDate: rule.endDate }),
    ...(rule.count !== undefined && { count: rule.count }),
  };
}

export function rowToTask(row: TaskRow): Task {
  lastKnownTaskStatus.set(row.id, row.status);
  const recurrence = parseRecurrence(row.recurrence);
  return {
    id: row.id,
    listId: row.list_id,
    title: row.title,
    status: row.status,
    priority: row.priority,
    createdBy: row.created_by,
    assignees: [...row.assignees],
    order: row.sort_order,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    ...(row.description != null && { description: row.description }),
    ...(row.emoji != null && { emoji: row.emoji }),
    ...(row.category_id != null && { categoryId: row.category_id }),
    ...(row.due_date != null && { dueDate: row.due_date }),
    ...(row.due_time != null && { dueTime: row.due_time.slice(0, 5) }),
    ...(row.reminder_minutes != null && {
      reminderMinutes: row.reminder_minutes,
    }),
    ...(recurrence !== undefined && { recurrence }),
  };
}

/**
 * Map a `task_lists` row to a {@link TaskList}.
 *
 * READ THE APPEARANCE BACK, NEVER ECHO IT. `emoji` and `color` are taken from
 * the row like every other field, and that is the whole fix for the tile that
 * turned grey a heartbeat after save. The store swaps its optimistic list for
 * whatever this function returns (`reconcileTaskList`), so a field this mapper
 * cannot produce is a field the user watches vanish one round trip after
 * pressing save — and re-attaching the caller's input to the returned object
 * instead would only move the loss to the next `load()`, which is where the
 * calendar labels' echoed emoji went. Both columns exist as of migration 028;
 * they are nullable, so the same `!= null` spread `rowToTask` uses for
 * `tasks.emoji` applies here.
 */
export function rowToTaskList(row: TaskListRow): TaskList {
  return {
    id: row.id,
    title: row.name,
    isArchived: row.is_archived,
    createdBy: row.owner_id,
    memberIds: [...row.member_ids],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    ...(row.description != null && { description: row.description }),
    ...(row.emoji != null && { emoji: row.emoji }),
    ...(row.color != null && { color: row.color }),
  };
}

function rowToCategory(row: TaskCategoryRow): TaskCategory {
  return {
    id: row.id,
    name: row.name,
    color: row.color,
    ...(row.emoji != null && { emoji: row.emoji }),
  };
}

/** True when the browser reports no network connectivity. */
function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Canonical UUID shape — Postgres `uuid` columns reject anything else. */
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Keep only valid profile UUIDs in an assignee list. Non-uuid ids (the
 * `member-vladi` default CreateTaskModal falls back to when no
 * `currentUserId` prop is threaded) must never reach the `tasks.assignees`
 * uuid[] column. When the creator was only present via such a fallback,
 * substitute the resolved profile id so the "assign to self" intent
 * survives.
 */
function sanitizeAssignees(
  assignees: readonly string[] | undefined,
  creatorId: string
): string[] {
  if (!assignees) {
    return [];
  }
  const valid = assignees.filter((id) => UUID_PATTERN.test(id));
  if (valid.length < assignees.length && !valid.includes(creatorId)) {
    valid.push(creatorId);
  }
  return valid;
}

/**
 * What a write throws when it never reached Postgres and there is no queue to
 * put it on.
 *
 * `updateTaskList`, `deleteTaskList` and (through it) `archiveTaskList` have
 * neither an `isOffline()` gate nor a transient fallback — Wave 4 never covered
 * the list writes, and giving them queue branches is new behaviour that belongs
 * in its own change. But now that a refused write throws, a wire failure on
 * those three throws too, and telling a user whose phone is in a tunnel that
 * they lack permission would be a fresh lie in the exact place this change
 * exists to stop lying. So they classify their own failure like every other
 * write and say THIS instead of the permission sentence. The refusal strings
 * stay inline at their throw sites; this one is shared because three methods
 * say it verbatim.
 */
const NO_CONNECTION_MESSAGE =
  'Няма връзка със сървъра: промяната не беше запазена. Опитайте отново.';

/**
 * The {@link Task} an UNSENT update answers with.
 *
 * NOT `rowToTask(… as TaskRow)`, and that is not a style preference.
 * {@link rowToTask} opens by WRITING `lastKnownTaskStatus.set(row.id,
 * row.status)`, so mapping a fabricated row through it would stamp this
 * function's `?? 'pending'` default into the offline-toggle recovery map for a
 * task that is actually `completed`. The next offline tap would then compute
 * `pending → completed`, queue a patch that changes nothing, and the user's
 * tick would come back wrong on replay. Do not "simplify" this into a mapper
 * call: there is no type error and no failing test standing in the way.
 *
 * FABRICATED FIELDS, spelled out so nobody mistakes them for data: `listId`,
 * `createdBy`, `title`, `priority` and `order` fall back to empty/default
 * values whenever the patch does not carry the column, `createdAt`/`updatedAt`
 * are this device's clock rather than the server's, and `recurrence` is dropped
 * entirely. `status` is the one field taken seriously — the patch first, then
 * the last status this session actually saw — because the offline toggle path
 * reads it back.
 *
 * This is safe ONLY because every current caller discards the queued-path
 * return value: the store applies its own optimistic patch and throws this
 * object away. Nothing may reconcile store state from it, the way
 * `reconcileTask` already reconciles from a CREATE's return.
 */
function synthesizeTask(taskId: string, patch: TablesUpdate<'tasks'>): Task {
  const now = new Date().toISOString();
  return {
    id: taskId,
    listId: patch.list_id ?? '',
    title: patch.title ?? '',
    status: patch.status ?? lastKnownTaskStatus.get(taskId) ?? 'pending',
    priority: patch.priority ?? 'medium',
    createdBy: patch.created_by ?? '',
    assignees: patch.assignees ? [...patch.assignees] : [],
    order: patch.sort_order ?? 0,
    createdAt: now,
    updatedAt: now,
    ...(patch.description != null && { description: patch.description }),
    ...(patch.emoji != null && { emoji: patch.emoji }),
    ...(patch.category_id != null && { categoryId: patch.category_id }),
    ...(patch.due_date != null && { dueDate: patch.due_date }),
    ...(patch.due_time != null && { dueTime: patch.due_time.slice(0, 5) }),
    ...(patch.reminder_minutes != null && {
      reminderMinutes: patch.reminder_minutes,
    }),
  };
}

/**
 * What a DELETE that matched no rows turned out to mean, once asked again.
 *
 * See {@link SupabaseTodoService.classifyEmptyDelete} for why the second
 * question has to be asked at all.
 */
type EmptyDeleteVerdict = 'refused' | 'already-gone' | 'unreachable';

// ============================================
// Supabase Todo Service
// ============================================

export class SupabaseTodoService implements ITodoService {
  private get client() {
    return getSupabaseClient();
  }

  /**
   * Resolve the authenticated profile id (= Supabase auth uid).
   *
   * Both branches are local reads now. {@link getCurrentProfileId} was an
   * `auth.getUser()` round trip, and that round trip sat ABOVE the offline gate
   * in {@link SupabaseTodoService.createTask}: on a captive portal or a dead
   * uplink it threw before either the gate or the transport-failure fallback
   * could queue the insert, so the task was lost rather than queued. It reads
   * the persisted session directly now; the explicit `auth.getSession()` below
   * stays as the offline branch so a resolver failure of any other kind still
   * yields the signed-in uuid. The calendar service resolves its author the
   * same way.
   */
  private async resolveProfileId(): Promise<string> {
    if (!isOffline()) {
      return getCurrentProfileId();
    }
    const { data } = await this.client.auth.getSession();
    const profileId = data.session?.user.id;
    if (!profileId) {
      throw new Error(
        'Няма активна сесия: не може да се определи текущият потребител.'
      );
    }
    return profileId;
  }

  // =====================
  // Offline enqueue paths
  // =====================
  //
  // One per operation, so the `navigator.onLine === false` gate and the
  // transport-failure fallback below it queue byte-identical entries. They were
  // inline before, which meant only the first of the two could exist.

  /**
   * Queue a task INSERT for replay and answer with the optimistic row.
   *
   * `sort_order` is taken from the insert when the online path already computed
   * one (a write that failed on the wire) and synthesised from the clock when it
   * did not (the offline gate, which cannot read the list's current maximum).
   */
  private async enqueueTaskInsert(
    insert: TablesInsert<'tasks'>
  ): Promise<Task> {
    const now = new Date().toISOString();
    const payload: TablesInsert<'tasks'> = {
      ...insert,
      id: crypto.randomUUID(),
      sort_order: insert.sort_order ?? Date.now() % 1_000_000,
      created_at: now,
      updated_at: now,
    };
    await enqueueSupabaseMutation({
      table: 'tasks',
      op: 'insert',
      payload: payload,
      description: `Създаване на задача „${insert.title}“`,
    });
    return rowToTask(payload as TaskRow);
  }

  /** Queue a task UPDATE for replay. */
  private async enqueueTaskUpdate(
    taskId: string,
    patch: TablesUpdate<'tasks'>
  ): Promise<void> {
    await enqueueSupabaseMutation({
      table: 'tasks',
      op: 'update',
      payload: patch,
      matchId: taskId,
      description: 'Промяна на задача',
    });
  }

  /** Queue a task DELETE for replay. */
  private async enqueueTaskDelete(taskId: string): Promise<void> {
    lastKnownTaskStatus.delete(taskId);
    await enqueueSupabaseMutation({
      table: 'tasks',
      op: 'delete',
      matchId: taskId,
      description: 'Изтриване на задача',
    });
  }

  // =====================
  // Task Operations
  // =====================

  async getTasks(listId?: string): Promise<Task[]> {
    const familyId = await getCurrentFamilyId();
    let query = this.client
      .from('tasks')
      .select('*')
      .eq('family_id', familyId)
      .order('sort_order', { ascending: true });
    if (listId !== undefined) {
      query = query.eq('list_id', listId);
    }
    const { data, error } = await query;
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map(rowToTask);
  }

  async getTask(taskId: string): Promise<Task | null> {
    const { task } = await this.readTask(taskId);
    return task;
  }

  /**
   * `getTask` with the failure kept rather than flattened into `null`.
   *
   * {@link ITodoService.getTask} answers `Task | null`, so a read that never
   * reached Postgres is indistinguishable from a row that is not there — and
   * `toggleTaskStatus` needs that distinction: on a dead uplink the browser
   * still reports as "online" it must fall through to the offline shape and
   * queue the flip, not return `null` into a store that discards it.
   */
  private async readTask(
    taskId: string
  ): Promise<{ readonly task: Task | null; readonly transient: boolean }> {
    const familyId = await getCurrentFamilyId();
    const { data, error, status } = await this.client
      .from('tasks')
      .select('*')
      .eq('family_id', familyId)
      .eq('id', taskId)
      .maybeSingle();
    if (error) {
      return { task: null, transient: isTransientWriteFailure(status, error) };
    }
    return { task: data ? rowToTask(data) : null, transient: false };
  }

  async createTask(input: CreateTaskInput): Promise<Task> {
    const familyId = await getCurrentFamilyId();
    // Resolve the creator server-side (auth uid = profiles.id), mirroring
    // supabaseCalendarService.createEvent. `input.createdBy` may carry the
    // `member-vladi` modal default, which is not a uuid and violates the
    // `created_by = auth.uid()` RLS check (migration 012). The resolved
    // id is also what the offline-queued payload persists for replay.
    const createdBy = await this.resolveProfileId();

    const baseInsert: TablesInsert<'tasks'> = {
      family_id: familyId,
      list_id: input.listId,
      title: sanitizeText(input.title),
      status: 'pending',
      priority: input.priority ?? 'medium',
      created_by: createdBy,
      assignees: sanitizeAssignees(input.assignees, createdBy),
      ...(input.description !== undefined && {
        description: sanitizeText(input.description),
      }),
      ...(input.emoji !== undefined && { emoji: input.emoji }),
      ...(input.categoryId !== undefined && { category_id: input.categoryId }),
      ...(input.dueDate !== undefined && { due_date: input.dueDate }),
      ...(input.dueTime !== undefined && { due_time: input.dueTime }),
      ...(input.reminderMinutes !== undefined && {
        reminder_minutes: input.reminderMinutes,
      }),
      ...(input.recurrence !== undefined && {
        recurrence: recurrenceToJson(input.recurrence),
      }),
    };

    // Offline: persist the insert for replay and return an optimistic task.
    if (isOffline()) {
      return this.enqueueTaskInsert(baseInsert);
    }

    // Compute next sort order within the list (highest sort_order + 1).
    const { data: lastRows } = await this.client
      .from('tasks')
      .select('sort_order')
      .eq('family_id', familyId)
      .eq('list_id', input.listId)
      .order('sort_order', { ascending: false })
      .limit(1);
    const nextOrder = lastRows && lastRows[0] ? lastRows[0].sort_order + 1 : 0;

    const insert: TablesInsert<'tasks'> = {
      ...baseInsert,
      sort_order: nextOrder,
    };

    const { data, error, status } = await this.client
      .from('tasks')
      .insert(insert)
      .select('*')
      .single();
    if (error || !data) {
      // The browser said "online" and the write still never landed — queue it
      // rather than losing it. A refusal falls through and throws.
      if (isTransientWriteFailure(status, error)) {
        return this.enqueueTaskInsert(insert);
      }
      throw new Error(error?.message ?? 'Неуспешно създаване на задача.');
    }
    return rowToTask(data);
  }

  async updateTask(
    taskId: string,
    updates: Partial<UpdateTaskInput>
  ): Promise<Task> {
    const patch: TablesUpdate<'tasks'> = {
      ...(updates.title !== undefined && {
        title: sanitizeText(updates.title),
      }),
      ...(updates.description !== undefined && {
        description: sanitizeText(updates.description),
      }),
      ...(updates.emoji !== undefined && { emoji: updates.emoji }),
      ...(updates.priority !== undefined && { priority: updates.priority }),
      ...(updates.categoryId !== undefined && {
        category_id: updates.categoryId,
      }),
      ...(updates.dueDate !== undefined && { due_date: updates.dueDate }),
      ...(updates.dueTime !== undefined && { due_time: updates.dueTime }),
      ...(updates.assignees !== undefined && {
        // Drop non-uuid entries (the `member-vladi` fallback) — see createTask.
        assignees: updates.assignees.filter((id) => UUID_PATTERN.test(id)),
      }),
      ...(updates.reminderMinutes !== undefined && {
        reminder_minutes: updates.reminderMinutes,
      }),
      ...(updates.recurrence !== undefined && {
        recurrence: recurrenceToJson(updates.recurrence),
      }),
      ...(updates.status !== undefined && { status: updates.status }),
    };

    const { task } = await this.writeTaskPatch(taskId, patch);
    return task;
  }

  /**
   * Send a `tasks` patch, and report BOTH the task and whether the patch is
   * actually in Postgres.
   *
   * THE `applied` BIT EXISTS FOR EXACTLY ONE CALLER and is deliberately kept
   * off {@link ITodoService}. `toggleTaskStatus` fans a completion
   * notification out through an Edge Function, and it must not announce a
   * completion the database has never seen — which is precisely what a queued
   * patch is. The public contract stays "resolved means safe, rejected means
   * not", with no third value for a caller to forget; this private shape
   * recovers the one extra bit for the one place that needs it.
   *
   * The branch order below is unchanged from when this body lived inside
   * `updateTask`, and it is load-bearing. `error === null` with no row is an
   * RLS-filtered UPDATE, not a wire failure — it must never be queued, because
   * a queued refusal replays forever and blocks the FIFO queue for every other
   * table — so the classifier is only consulted when there is an error envelope
   * to classify. What changed is only the fall-through: the refusal used to
   * `return null`, which `persistTodoMutation` resolves right past.
   */
  private async writeTaskPatch(
    taskId: string,
    patch: TablesUpdate<'tasks'>
  ): Promise<{ readonly task: Task; readonly applied: boolean }> {
    // A REFUSAL AND AN UNSENT WRITE MUST NOT READ THE SAME, so the "or is
    // already deleted" half stays on the update messages: an UPDATE that
    // matched zero rows cannot tell the two apart the way the deletes below
    // can, because it has no cheap second question to ask.
    const REFUSED =
      'Задачата не можа да бъде променена: нямате права или вече е изтрита.';

    // Offline: queue the patch for replay; answer with the patch dressed as a
    // task.
    //
    // Nothing is read back. The `getTask` that used to run HERE — after the
    // enqueue, over a network that is by definition down — was a guaranteed
    // failure dressed up as a lookup, and it only ever returned `null` into a
    // store that discards this value anyway. {@link synthesizeTask} is what
    // that branch answers with now, for the same reason `supabaseCalendarService
    // .updateEvent` answers with `synthesizeEvent`: a write that is safely
    // queued is not a failure, so it must not reject.
    if (isOffline()) {
      await this.enqueueTaskUpdate(taskId, patch);
      return { task: synthesizeTask(taskId, patch), applied: false };
    }

    const familyId = await getCurrentFamilyId();
    const { data, error, status } = await this.client
      .from('tasks')
      .update(patch)
      .eq('family_id', familyId)
      .eq('id', taskId)
      .select('*')
      .maybeSingle();
    // `error === null` with no row is an RLS-filtered UPDATE, not a wire
    // failure — it must never be queued, so the classifier is only consulted
    // when there is an error envelope to classify.
    if (error && isTransientWriteFailure(status, error)) {
      await this.enqueueTaskUpdate(taskId, patch);
      return { task: synthesizeTask(taskId, patch), applied: false };
    }
    if (error) {
      throw new Error(REFUSED, { cause: error });
    }
    if (!data) {
      throw new Error(REFUSED);
    }
    return { task: rowToTask(data), applied: true };
  }

  async deleteTask(taskId: string): Promise<void> {
    const REFUSED = 'Задачата не можа да бъде изтрита: нямате права.';

    if (isOffline()) {
      await this.enqueueTaskDelete(taskId);
      return;
    }
    const familyId = await getCurrentFamilyId();
    // `.select('id')` IS THE WHOLE POINT OF THIS QUERY'S SHAPE. A bare
    // `.delete().eq().eq()` comes back `{ error: null }` whether it removed the
    // row or RLS filtered it away, because a `USING` clause narrows the row set
    // rather than raising — so `return !error` reported success for a delete
    // Postgres refused, and the task stayed on screen until the next focus
    // refetch put it back.
    const { data, error, status } = await this.client
      .from('tasks')
      .delete()
      .eq('family_id', familyId)
      .eq('id', taskId)
      .select('id');
    if (error && isTransientWriteFailure(status, error)) {
      await this.enqueueTaskDelete(taskId);
      return;
    }
    if (error) {
      throw new Error(REFUSED, { cause: error });
    }
    if (!data || data.length === 0) {
      const verdict = await this.classifyEmptyDelete('tasks', familyId, taskId);
      if (verdict === 'unreachable') {
        await this.enqueueTaskDelete(taskId);
        return;
      }
      if (verdict === 'refused') {
        throw new Error(REFUSED);
      }
      // 'already-gone' — the other parent deleted it first. The user's intent
      // is satisfied, so this resolves like any other successful delete and the
      // cached status is dropped below.
    }
    // BELOW THE ZERO-ROW CHECK, NOT ABOVE IT, and that move is a fix in its own
    // right. Evicting the cached status on a refused delete makes the NEXT
    // offline toggle throw «Задачата не е заредена…» for a task that is plainly
    // still on screen, because {@link lastKnownTaskStatus} is the only thing an
    // offline flip can read the current status out of.
    lastKnownTaskStatus.delete(taskId);
  }

  /**
   * Ask whether a row a DELETE did not touch is still there, and turn the
   * answer into a verdict.
   *
   * WHY A SECOND ROUND TRIP. Zero rows back from a DELETE means one of exactly
   * two things — RLS refused the write, or the row was already gone — and the
   * two need opposite handling. Throwing on both would hand the user a
   * destructive toast for a delete that fully succeeded, and worse: the store's
   * rollback RE-INSERTS the row it just removed, so the family member who was
   * beaten to the delete by the other parent would watch a phantom task
   * reappear. Resolving on both is the silent failure this whole change exists
   * to end. So we ask.
   *
   * The question is sound because SELECT is family-wide on both `tasks` and
   * `task_lists` while every write policy over them is strictly narrower —
   * `tasks_delete_own_child` / `tasks_delete_parent` on the first, and on the
   * second the `FOR ALL` policy `task_lists_modify_parent`, which supplies the
   * DELETE `USING` clause rather than there being a dedicated DELETE policy.
   * A row that is still readable but did not delete can therefore only be a
   * policy refusal.
   *
   * THIS PUTS THE LIVE PATH IN AGREEMENT WITH THE REPLAY PATH RATHER THAN IN
   * CONTRADICTION WITH IT. `zeroRowOutcome` in `src/shared/hooks/
   * useOfflineQueue.ts` deliberately counts a zero-row replay DELETE as
   * SUCCESS, and explains at length why: the ordinary case is the other parent
   * having deleted the same task from their own device. A live delete that
   * called the same observation a failure would make the two disagree about the
   * identical wire response. Do not "fix" one of them to match the other; they
   * are already the same rule, reached from different amounts of information.
   *
   * A failing confirmation is classified rather than trusted: transient means
   * the caller may queue (or, for a caller with no queue, say so honestly), and
   * anything else is reported as a refusal — the conservative direction is
   * always to surface a failure, never to swallow one.
   */
  private async classifyEmptyDelete(
    table: 'tasks' | 'task_lists',
    familyId: string,
    rowId: string
  ): Promise<EmptyDeleteVerdict> {
    // Spelled as a ternary over two literal table names rather than
    // `from(table)`: the typed client resolves its row types from the literal,
    // and a variable table name erases them.
    const query =
      table === 'tasks'
        ? this.client.from('tasks').select('id')
        : this.client.from('task_lists').select('id');
    const { data, error, status } = await query
      .eq('family_id', familyId)
      .eq('id', rowId)
      .maybeSingle();
    if (error) {
      return isTransientWriteFailure(status, error) ? 'unreachable' : 'refused';
    }
    return data ? 'refused' : 'already-gone';
  }

  /**
   * Flip a task between `completed` and `pending`.
   *
   * THE OFFLINE BRANCH COMES FIRST, and that ordering is the entire fix. This
   * method used to open with a `getTask` round trip to learn the current
   * status, so offline it never reached an enqueue at all: postgrest-js folded
   * the failed fetch into `{ data: null, error }`, `getTask` answered `null`,
   * and the toggle returned `null` — no queue entry, no throw, therefore no
   * rollback and no toast. The checkbox stayed ticked until `useRefetchOnFocus`
   * replaced `tasks` wholesale and took the tick away with it.
   *
   * Offline the next status is flipped out of {@link lastKnownTaskStatus} and
   * handed to {@link updateTask}, which queues the patch. A task this session
   * has never mapped cannot be flipped blind, so that throws — the store rolls
   * its optimistic tick back and toasts, which is the honest answer, and it is
   * unreachable in practice because a task that was never loaded is not on
   * screen to tap.
   *
   * The ONLINE branch keeps the read, because the `notify-task-completed`
   * payload needs the previous row — but it reads through {@link readTask}, so
   * a read that never reached Postgres while the browser still reported
   * "online" (a captive portal, a dead radio, a 5xx) falls through to the same
   * `lastKnownTaskStatus` flip instead of answering `null`. Returning `null`
   * there was the online half of the very bug the offline branch above fixes:
   * `persistTodoMutation` only rolls back inside `.catch()`, and a resolved
   * `null` never reaches it. The remaining `null` — a row the database simply
   * will not hand over — now throws for that same reason.
   */
  async toggleTaskStatus(taskId: string): Promise<Task> {
    if (isOffline()) {
      const known = lastKnownTaskStatus.get(taskId);
      if (known === undefined) {
        throw new Error(
          'Задачата не е заредена: статусът ѝ не може да бъде променен офлайн.'
        );
      }
      const offlineNext: TaskStatus =
        known === 'completed' ? 'pending' : 'completed';
      lastKnownTaskStatus.set(taskId, offlineNext);
      return this.updateTask(taskId, { status: offlineNext });
    }

    const { task: current, transient } = await this.readTask(taskId);
    if (!current) {
      // The read never reached Postgres — a dead uplink, a captive portal, a
      // 5xx — while `navigator.onLine` said "online", so the offline gate above
      // did not fire. Flip out of the local map exactly as that gate would;
      // `updateTask`'s own transport fallback queues the patch. Without this
      // the toggle resolved `null`, `persistTodoMutation` never reached its
      // `.catch()`, and the tick stayed on screen with nothing queued behind it
      // until the next refetch quietly took it away.
      const known = transient ? lastKnownTaskStatus.get(taskId) : undefined;
      if (known !== undefined) {
        const recovered: TaskStatus =
          known === 'completed' ? 'pending' : 'completed';
        lastKnownTaskStatus.set(taskId, recovered);
        return this.updateTask(taskId, { status: recovered });
      }
      // Not transient, and no local status to fall back on: the row is not
      // there for this account. That is a refusal or a task another member has
      // deleted, and either way the tick the user just made is not going to be
      // saved — so say so rather than resolving `null` past the store's
      // `.catch()` and leaving the tick on screen until the next refetch.
      throw new Error(
        'Задачата не е намерена: може да е изтрита от друг член на семейството.'
      );
    }
    const nextStatus = current.status === 'completed' ? 'pending' : 'completed';
    const { task: updated, applied } = await this.writeTaskPatch(taskId, {
      status: nextStatus,
    });

    // F5: fan out "task completed" notifications through the Edge Function.
    // Fire-and-forget — notification delivery must never block the toggle.
    //
    // GATED ON `applied`, NOT ON `!isOffline()`, and the difference is the
    // captive portal. `navigator.onLine` is `true` there, so the old condition
    // announced a completion for a patch the transient fallback had just put on
    // the offline queue — a push about a tick Postgres has never seen, for a
    // task whose row still says `pending`. `applied` is true only when the
    // UPDATE came back with the row, which subsumes the connectivity check
    // entirely.
    if (applied && nextStatus === 'completed') {
      void this.notifyTaskCompleted(current, updated).catch(
        (error: unknown) => {
          monitoringService.captureError(
            error instanceof Error ? error : new Error(String(error)),
            {
              tags: { feature: 'todo-list', action: 'notifyTaskCompleted' },
              level: 'warning',
            }
          );
        }
      );
    }

    return updated;
  }

  /**
   * Invoke the `notify-task-completed` Edge Function with the DB-webhook
   * payload shape (type/record/old_record) so completion notifications reach
   * the creator and assignees.
   *
   * WHY THE RESULT IS CHECKED RATHER THAN AWAITED AND DROPPED. `functions
   * .invoke` does NOT reject on a non-2xx: supabase-js resolves with a
   * `FunctionsHttpError` sitting in the `error` slot. Discarding the whole
   * envelope therefore made this method resolve on every refusal the handler
   * can return — 401 from `resolveCaller`, 403 for a caller outside the family,
   * 404 for a task that is already gone, 405 for a wrong method — and the
   * `.catch()` that `toggleTaskStatus` wraps the call in could never run. Every
   * undelivered notification was invisible, in the one code path where nobody
   * is watching for the absence of a push.
   *
   * Rethrowing keeps the call fire-and-forget: the toggle itself has already
   * been persisted, so the only consumer is `monitoringService`, and nothing
   * user-facing changes.
   */
  private async notifyTaskCompleted(
    previous: Task,
    completed: Task
  ): Promise<void> {
    const familyId = await getCurrentFamilyId();
    const actorId = await getCurrentProfileId();
    const response = await this.client.functions.invoke<unknown>(
      'notify-task-completed',
      {
        body: {
          type: 'UPDATE',
          record: {
            id: completed.id,
            family_id: familyId,
            title: completed.title,
            status: completed.status,
            created_by: completed.createdBy,
            assignees: [...completed.assignees],
          },
          old_record: {
            id: previous.id,
            family_id: familyId,
            title: previous.title,
            status: previous.status,
            created_by: previous.createdBy,
            assignees: [...previous.assignees],
          },
          actor_id: actorId,
        },
      }
    );
    // `FunctionsResponse`'s failure variant types `error` as `any`, so it is
    // narrowed here rather than dereferenced blind — the same cast the auth and
    // family services put on their invokes. No `context.json()` unwrapping:
    // nothing here reaches the user, the message goes to monitoring.
    const error = response.error as { message: string } | null;
    if (error) {
      throw new Error(`notify-task-completed failed: ${error.message}`);
    }
  }

  // =====================
  // Task List Operations
  // =====================

  async getTaskLists(): Promise<TaskList[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('task_lists')
      .select('*')
      .eq('family_id', familyId)
      .order('created_at', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map(rowToTaskList);
  }

  async getActiveTaskLists(): Promise<TaskList[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('task_lists')
      .select('*')
      .eq('family_id', familyId)
      .eq('is_archived', false)
      .order('created_at', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map(rowToTaskList);
  }

  async getTaskList(listId: string): Promise<TaskList | null> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('task_lists')
      .select('*')
      .eq('family_id', familyId)
      .eq('id', listId)
      .maybeSingle();
    if (error || !data) {
      return null;
    }
    return rowToTaskList(data);
  }

  async createTaskList(input: CreateTaskListInput): Promise<TaskList> {
    const familyId = await getCurrentFamilyId();
    // Resolve the owner server-side — `input.createdBy` may be the
    // `member-vladi` modal default, which is not a uuid (see createTask).
    const ownerId = await getCurrentProfileId();
    const insert: TablesInsert<'task_lists'> = {
      family_id: familyId,
      name: sanitizeText(input.title),
      owner_id: ownerId,
      is_archived: false,
      member_ids: input.memberIds ? [...input.memberIds] : [],
      ...(input.description !== undefined && {
        description: sanitizeText(input.description),
      }),
      // The picked appearance goes into the row. `!= null` covers both shapes
      // the modal can send — the field absent, and `null` for a cleared emoji
      // — and both mean the same thing on an INSERT: leave the nullable column
      // at NULL. Neither value is sanitized: an emoji and a `#rrggbb` swatch
      // are picked from closed lists (EmojiPicker, DEFAULT_COLORS), never
      // typed, and `sanitizeText` normalises whitespace, which is not
      // something either one wants done to it.
      ...(input.emoji != null && { emoji: input.emoji }),
      ...(input.color != null && { color: input.color }),
    };
    const { data, error } = await this.client
      .from('task_lists')
      .insert(insert)
      .select('*')
      .single();
    if (error || !data) {
      throw new Error(error?.message ?? 'Неуспешно създаване на списък.');
    }
    return rowToTaskList(data);
  }

  async updateTaskList(
    listId: string,
    updates: Partial<UpdateTaskListInput>
  ): Promise<TaskList> {
    const familyId = await getCurrentFamilyId();
    const patch: TablesUpdate<'task_lists'> = {
      ...(updates.title !== undefined && { name: sanitizeText(updates.title) }),
      ...(updates.description !== undefined && {
        description: sanitizeText(updates.description),
      }),
      ...(updates.isArchived !== undefined && {
        is_archived: updates.isArchived,
      }),
      ...(updates.memberIds !== undefined && {
        member_ids: [...updates.memberIds],
      }),
      // Three states, not two. `undefined` means this edit did not touch the
      // field, so the column is left alone; `null` means the user pressed
      // "Изчисти" in the emoji picker and the column must actually become
      // NULL; a string replaces it. Testing `!= null` here — the test the
      // INSERT above wants — would silently turn "clear it" into "keep it",
      // which is the shape of the adjacent label defect.
      ...(updates.emoji !== undefined && { emoji: updates.emoji }),
      ...(updates.color !== undefined && { color: updates.color }),
    };
    // `status` joins the destructuring so the failure can be CLASSIFIED. This
    // method has no offline queue branch — see {@link NO_CONNECTION_MESSAGE} —
    // so classification here does not choose between queueing and throwing, it
    // chooses which true sentence to throw.
    const { data, error, status } = await this.client
      .from('task_lists')
      .update(patch)
      .eq('family_id', familyId)
      .eq('id', listId)
      .select('*')
      .maybeSingle();
    if (error && isTransientWriteFailure(status, error)) {
      throw new Error(NO_CONNECTION_MESSAGE, { cause: error });
    }
    // Zero rows with no error is the refusal this whole change is about: RLS
    // filtered the UPDATE instead of raising, `task_lists_modify_parent` being
    // `FOR ALL (family AND is_parent())`, so a child renaming a shared list got
    // `{ data: null, error: null }` — and `return null` turned that into a
    // resolved promise the store's `.catch()` never saw. The rename stayed on
    // screen until the next focus refetch quietly reverted it.
    const REFUSED =
      'Списъкът не можа да бъде променен: нямате права или вече е изтрит.';
    if (error) {
      throw new Error(REFUSED, { cause: error });
    }
    if (!data) {
      throw new Error(REFUSED);
    }
    return rowToTaskList(data);
  }

  async deleteTaskList(listId: string): Promise<void> {
    const REFUSED = 'Списъкът не можа да бъде изтрит: нямате права.';

    const familyId = await getCurrentFamilyId();
    // `.select('id')` for the same reason as in {@link deleteTask}: without the
    // rows back, an RLS-filtered delete is indistinguishable from a successful
    // one, and `return !error` called both of them success.
    const { data, error, status } = await this.client
      .from('task_lists')
      .delete()
      .eq('family_id', familyId)
      .eq('id', listId)
      .select('id');
    if (error && isTransientWriteFailure(status, error)) {
      throw new Error(NO_CONNECTION_MESSAGE, { cause: error });
    }
    if (error) {
      throw new Error(REFUSED, { cause: error });
    }
    if (!data || data.length === 0) {
      const verdict = await this.classifyEmptyDelete(
        'task_lists',
        familyId,
        listId
      );
      if (verdict === 'unreachable') {
        // No queue branch to fall back on, so the honest answer is the
        // connection sentence rather than a permission claim we cannot support.
        throw new Error(NO_CONNECTION_MESSAGE);
      }
      if (verdict === 'refused') {
        throw new Error(REFUSED);
      }
      // 'already-gone' — another parent deleted the list first. Nothing to
      // report: the caller asked for it to be gone and it is.
    }
  }

  /**
   * Archiving is one particular list update, and it stays exactly that.
   *
   * The delegate's contract IS this method's contract — a refused archive
   * rejects because {@link updateTaskList} rejects, with the same sentence.
   * There is deliberately no try/catch, no unwrap and no rewrap here: every one
   * of those would be a place for the refusal to be flattened back into a
   * resolved value, which is the bug.
   */
  async archiveTaskList(listId: string): Promise<TaskList> {
    return this.updateTaskList(listId, { isArchived: true });
  }

  // =====================
  // Category Operations
  // =====================

  async getCategories(): Promise<TaskCategory[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('task_categories')
      .select('*')
      .eq('family_id', familyId)
      .order('name', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map(rowToCategory);
  }

  // =====================
  // Statistics
  // =====================

  async getListStats(listId: string): Promise<ListStats> {
    const tasks = await this.getTasks(listId);
    return calculateListStats(tasks, listId);
  }

  // =====================
  // Utility
  // =====================

  reset(): void {
    // Persistent data is not reset client-side; the in-memory status cache is,
    // so a family switch or a sign-out cannot leave another scope's statuses
    // behind for an offline toggle to flip.
    lastKnownTaskStatus.clear();
  }
}
